<template>
  <div class="vertical-landing">
    <header class="vl-header">
      <div class="vl-header-inner">
        <NuxtLink to="/" class="vl-logo">Comanda<span>.</span></NuxtLink>
        <NuxtLink to="/auth?modo=registro" class="btn-primary">Empezar gratis</NuxtLink>
      </div>
    </header>

    <!-- Hero específico vertical -->
    <section class="vl-hero">
      <div class="vl-hero-eyebrow">{{ vertical.eyebrow }}</div>
      <h1>{{ vertical.headline }}</h1>
      <p class="vl-sub">{{ vertical.sub }}</p>
      <div class="vl-cta">
        <NuxtLink to="/auth?modo=registro" class="btn-primary">Empezar gratis · 14 días</NuxtLink>
        <NuxtLink to="/#calculadora" class="btn-secondary">Calcular lo que te ahorro</NuxtLink>
      </div>
    </section>

    <!-- Tres dolores específicos del vertical -->
    <section class="vl-pains">
      <h2>Lo que más duele en {{ vertical.name }}</h2>
      <div class="vl-pains-grid">
        <article v-for="p in vertical.pains" :key="p.title" class="vl-pain">
          <div class="vl-pain-stat">{{ p.stat }}</div>
          <h3>{{ p.title }}</h3>
          <p>{{ p.body }}</p>
        </article>
      </div>
    </section>

    <!-- Cómo lo resolvemos -->
    <section class="vl-solutions">
      <div class="section-label">Cómo te lo resolvemos</div>
      <h2>{{ vertical.solutionsTitle }}</h2>
      <ul class="vl-solutions-list">
        <li v-for="s in vertical.solutions" :key="s">{{ s }}</li>
      </ul>
    </section>

    <!-- Pricing recap — precios canónicos (docs/MENSAJES-MAESTROS.md §5) -->
    <section class="vl-pricing">
      <div class="vl-pricing-card">
        <div class="vl-pricing-eyebrow">Plan Pro · el más elegido</div>
        <div class="vl-pricing-price">49 €<span>/mes + IVA</span></div>
        <p>Las 3 puertas: caja que cuadra, cuenta dividida y camarero nuevo en una tarde. Sin hardware, sin permanencia. Tu precio congelado 3 años.</p>
        <NuxtLink to="/auth?modo=registro" class="btn-primary">Empezar gratis · 14 días</NuxtLink>
        <p class="vl-pricing-alt">También Esencial (29 €/mes) para bares de menos de 10 mesas y Multi (89 €/mes por local) para cadenas.</p>
      </div>
    </section>

    <!-- Volver a la landing principal -->
    <section class="vl-back">
      <NuxtLink to="/">← Ver todas las funciones de ComandaPlus</NuxtLink>
    </section>

    <footer class="vl-footer">
      <span>ComandaPlus &copy; 2026</span>
      <div>
        <NuxtLink to="/legal/terminos">Términos</NuxtLink>
        <NuxtLink to="/legal/privacidad">Privacidad</NuxtLink>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
definePageMeta({ layout: false })

const route = useRoute()
const slug = computed(() => String(route.params.vertical || '').toLowerCase())

// Config por vertical. Cada uno con copy específico, dolores, soluciones.
// Datos respaldados por docs/INVESTIGACION-DESAFIOS-HOSTELEROS-ESPANA-2026.md.
const verticales: Record<string, any> = {
  restaurantes: {
    name: 'restaurantes',
    eyebrow: 'Para restaurantes con servicio en mesa',
    headline: 'Tu sala funcionando sin gritos. Tu cocina sin errores. Y la mesa de 8 paga en 30 segundos.',
    sub: 'Cada vez más restaurantes cuelgan el cartel de "no dividimos cuentas". No por mala voluntad: es que no pueden. ComandaPlus te resuelve los tres problemas que más cuestan en horas pico.',
    pains: [
      {
        stat: '3, 4 o 5 cervezas',
        title: 'La mesa de 8 que divide cuenta te para el servicio',
        body: '"El establecimiento siempre pierde tres, cuatro o cinco cervezas en la confusión de separar comandas y pagos" (camarero, El Español, jul 2025). Cada comensal con su QR paga lo suyo. La mesa se libera sola.',
      },
      {
        stat: 'horas pico',
        title: 'Comandas mal escritas, alergias ignoradas',
        body: 'Una alergia mal anotada es una mala reseña que vive para siempre. Aquí van directas a cocina con alergias en rojo, modificaciones por escrito, tiempos visibles.',
      },
      {
        stat: '10–15 % de las compras',
        title: 'Mermas comiendo el margen',
        body: 'El desperdicio alimentario es el 10–15 % de las compras (Loomis Pay). El diagnóstico de carta clasifica platos en estrella, caballo o peso muerto. Sabes qué quitar.',
      },
    ],
    solutionsTitle: 'Lo que ComandaPlus pone en tu sala',
    solutions: [
      'Cuenta dividida real desde el móvil del cliente — sin que el camarero toque la calculadora',
      'Comanda directa a cocina con alergias destacadas en rojo y tiempos visibles',
      'Diagnóstico automático de carta — qué plato vende y qué plato te cuesta dinero',
      'Cierre Z automático que cuadra solo, sin Excel ni calculadora',
      'Funciona en el móvil que ya tienes — sin instalar, sin hardware, sin técnico',
      'VeriFactu en desarrollo, listo antes del plazo de enero 2027 e incluido sin coste',
    ],
  },
  bares: {
    name: 'bares y tabernas',
    eyebrow: 'Para bares y tabernas',
    headline: 'Tu caja cuadra el lunes. Y si no cuadra, sabes con qué camarero y en qué turno se fue el dinero.',
    sub: 'Un bar mediano pierde más de 3.000 € al año en descuadres invisibles (Ninja TPV). Y casi nunca sabe dónde se fueron. Si tu negocio es pequeño, tienes los dolores más caros.',
    pains: [
      {
        stat: '3.000 €/año',
        title: 'Descuadres de caja que no ves',
        body: '"Buenas chicos, hoy nuevamente le falta dinero a la caja, no puede ser que en menos de dos semanas ya hagan falta casi 60 euros" (mensaje real de un dueño a sus camareros). Cierre Z automático, gastos al instante, descuadre a la vista.',
      },
      {
        stat: '100.000 vacantes',
        title: 'El camarero nuevo tarda una semana en aprender… si aguanta',
        body: '"Es que no te aguantan un turno de 10 horas" (hostelero, El Español, jul 2025). Aquí el sistema es tan fácil como WhatsApp: tocas el plato, tocas la mesa, ya está. Sirve la primera tarde.',
      },
      {
        stat: '0 € hardware',
        title: 'Bar pequeño = no tienes presupuesto para instalación',
        body: 'Otros sistemas piden tablet, impresora, datáfono propio e instalación con técnico. Aquí: tu móvil, los QR impresos, una tarde. Esto es todo.',
      },
    ],
    solutionsTitle: 'Lo que ComandaPlus pone en tu barra',
    solutions: [
      'Cierre Z automático con desglose por camarero, turno y método de pago',
      'Gastos de caja registrados al instante, sin Excel',
      'Funciona en el móvil que ya tienes — tablet vieja, portátil, lo que sea con navegador',
      'Modo barra rápido para tickets cortos sin reserva',
      'Cuenta dividida disponible para grupos (cuando entren 8 amigos a tomar algo)',
      'Sin permanencia — pruebas 14 días gratis y decides',
      'VeriFactu en desarrollo, listo antes del plazo e incluido sin coste',
    ],
  },
  cafeterias: {
    name: 'cafeterías',
    eyebrow: 'Para cafeterías y desayunos',
    headline: 'Tickets rápidos sin colas. Carta digital con QR. Caja que no se descuadra.',
    sub: 'En una cafetería el ticket medio es bajo y el flujo es continuo. Cada minuto perdido en cobro es uno menos atendiendo. ComandaPlus está pensado para que el camarero cobre con un tap y siga.',
    pains: [
      {
        stat: '2–3 €',
        title: 'Tickets de margen mínimo, cero tiempo para errores',
        body: 'Cada cobro lento te cuesta el siguiente cliente. Un café no aguanta 5 minutos de espera en caja. ComandaPlus tiene modo barra optimizado para tickets cortos.',
      },
      {
        stat: '74%',
        title: 'Adopción digital del sector — pero las cafeterías van por detrás',
        body: 'El 74% de la hostelería ya está digitalizada (Recipok), pero el bar/cafetería pequeño sigue rezagado. La fricción es la instalación, no el querer.',
      },
      {
        stat: '0 menús físicos',
        title: 'Carta digital actualizable al instante',
        body: 'Cambias precio, añades nuevo bocadillo, marcas un plato como agotado — todos los QR de las mesas ya muestran la carta nueva. Sin reimpresiones.',
      },
    ],
    solutionsTitle: 'Lo que ComandaPlus pone en tu cafetería',
    solutions: [
      'Modo barra para tickets rápidos sin asignar mesa',
      'Carta digital actualizable en segundos — adiós a reimprimir',
      'Cierre Z automático con desglose por camarero, turno y método de pago',
      'Comanda directa a cocina/barra para que el camarero no grite por encima del ruido',
      'Sin hardware: tu móvil + los QR impresos = ya está',
      'VeriFactu en desarrollo, listo antes del plazo e incluido sin coste',
    ],
  },
}

const vertical = computed(() => verticales[slug.value] || verticales.restaurantes)

// 404-friendly: si el slug no existe, redirigir a la landing principal (manejado por SSR)
if (!verticales[slug.value]) {
  throw createError({ statusCode: 404, statusMessage: 'Vertical no encontrado', fatal: false })
}

// SEO problema-first: ni "TPV" ni "VeriFactu" en el título
// (docs/MENSAJES-MAESTROS.md §3, §8.4 y §11).
const seoTitles: Record<string, string> = {
  bares: 'Cómo evitar descuadres de caja en tu bar | ComandaPlus',
  restaurantes: 'Dividir la cuenta en tu restaurante, sin calculadora | ComandaPlus',
  cafeterias: 'Carta digital con QR para cafeterías | ComandaPlus',
}

useHead(() => ({
  title: seoTitles[slug.value] || `ComandaPlus para ${vertical.value.name}`,
  meta: [
    { name: 'description', content: vertical.value.sub },
    { property: 'og:title', content: `ComandaPlus para ${vertical.value.name}` },
    { property: 'og:description', content: vertical.value.sub },
    { property: 'og:type', content: 'website' },
    { property: 'og:locale', content: 'es_ES' },
  ],
}))
</script>

<style scoped>
.vertical-landing {
  font-family: 'Geist', 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  background: #F7F6F2;
  color: #0B0B0A;
  line-height: 1.6;
  font-size: 16px;
  min-height: 100vh;
}
.vl-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(247, 246, 242, 0.96);
  border-bottom: 1px solid #E4E2DC;
  backdrop-filter: blur(10px);
}
.vl-header-inner {
  max-width: 1100px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.vl-logo {
  font-size: 1.4rem;
  font-weight: 800;
  color: #0B0B0A;
  text-decoration: none;
  letter-spacing: -0.02em;
}
.vl-logo span { color: #D14A2C; }

.btn-primary, .btn-secondary {
  padding: 0.75rem 1.4rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  transition: background 120ms ease, transform 80ms ease;
  border: 1px solid #0B0B0A;
}
.btn-primary { background: #0B0B0A; color: #F7F6F2; }
.btn-primary:hover { background: #2A2A28; transform: translateY(-1px); }
.btn-secondary { background: transparent; color: #0B0B0A; }
.btn-secondary:hover { background: #E4E2DC; }

/* Hero */
.vl-hero {
  max-width: 900px;
  margin: 0 auto;
  padding: 5rem 2rem 3rem;
  text-align: center;
}
/* #A8391F sobre fondo claro = 5,94:1 (#D14A2C se quedaba en 4,11:1). */
.vl-hero-eyebrow {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #A8391F;
  font-weight: 600;
  margin-bottom: 1rem;
}
.vl-hero h1 {
  font-size: clamp(2rem, 4.5vw, 3rem);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
  margin: 0 0 1.25rem 0;
  color: #0B0B0A;
}
.vl-sub {
  font-size: 1.1rem;
  color: #3F1A0F;
  max-width: 720px;
  margin: 0 auto 2rem;
  line-height: 1.55;
}
.vl-cta { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }

/* Pains */
.vl-pains {
  max-width: 1100px;
  margin: 0 auto;
  padding: 4rem 2rem 2rem;
}
.vl-pains h2 {
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0 0 2.5rem 0;
  text-align: center;
}
.vl-pains-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}
.vl-pain {
  background: #fff;
  border: 1px solid #E4E2DC;
  border-left: 3px solid #D14A2C;
  border-radius: 4px;
  padding: 1.75rem;
}
.vl-pain-stat {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 1.6rem;
  font-weight: 700;
  color: #D14A2C;
  margin-bottom: 0.5rem;
  letter-spacing: -0.01em;
  font-feature-settings: "tnum";
  font-variant-numeric: tabular-nums;
}
.vl-pain h3 {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: #0B0B0A;
}
.vl-pain p {
  margin: 0;
  font-size: 0.92rem;
  color: #3F1A0F;
  line-height: 1.5;
}

/* Solutions */
.vl-solutions {
  max-width: 900px;
  margin: 0 auto;
  padding: 3rem 2rem 4rem;
  text-align: center;
}
.section-label {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #6B6862;
  margin-bottom: 0.75rem;
  font-weight: 500;
}
.vl-solutions h2 {
  font-size: clamp(1.4rem, 2.6vw, 1.9rem);
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0 0 2rem 0;
}
.vl-solutions-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  text-align: left;
}
.vl-solutions-list li {
  background: #fff;
  border: 1px solid #E4E2DC;
  border-radius: 4px;
  padding: 0.95rem 1.25rem;
  position: relative;
  padding-left: 2.4rem;
  color: #0B0B0A;
}
.vl-solutions-list li::before {
  content: "→";
  position: absolute;
  left: 1rem;
  color: #D14A2C;
  font-weight: 700;
}

/* Pricing */
.vl-pricing {
  background: #0B0B0A;
  color: #F7F6F2;
  padding: 4rem 2rem;
}
.vl-pricing-card {
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
}
.vl-pricing-eyebrow {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #D14A2C;
  margin-bottom: 1rem;
  font-weight: 600;
}
.vl-pricing-price {
  font-size: 4rem;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1;
  margin-bottom: 0.5rem;
}
.vl-pricing-price span {
  font-size: 1rem;
  font-weight: 400;
  color: #B5B0A6;
  margin-left: 0.4rem;
}
.vl-pricing p {
  color: #C9C5BC;
  margin-bottom: 1.5rem;
}
.vl-pricing-alt {
  font-size: 0.9rem;
  color: #C9C5BC;
  margin: 1.25rem 0 0;
}
/* Blanco sobre #A8391F = 6,39:1 (con #D14A2C se quedaba en 4,44:1). */
.vl-pricing .btn-primary {
  background: #A8391F;
  border-color: #A8391F;
  color: #fff;
}
.vl-pricing .btn-primary:hover { background: #8E2F19; border-color: #8E2F19; }

/* Back */
.vl-back {
  text-align: center;
  padding: 2rem;
}
.vl-back a {
  color: #6B6862;
  text-decoration: none;
  font-size: 0.92rem;
}
.vl-back a:hover { color: #0B0B0A; }

/* Footer */
.vl-footer {
  border-top: 1px solid #E4E2DC;
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #6B6862;
}
.vl-footer a {
  color: #6B6862;
  text-decoration: none;
  margin-left: 1rem;
}
.vl-footer a:hover { color: #0B0B0A; }

@media (max-width: 768px) {
  .vl-hero { padding: 3rem 1.25rem 2rem; }
  .vl-pains, .vl-solutions { padding-left: 1.25rem; padding-right: 1.25rem; }
  .vl-pricing { padding: 3rem 1.25rem; }
  .vl-pricing-price { font-size: 3rem; }
  .vl-footer { flex-direction: column; gap: 0.5rem; align-items: flex-start; }
  .vl-footer a:first-child { margin-left: 0; }
}
</style>
